/**
 * BindSkillEffect — Conjuration Bind path active: summon a durable familiar.
 *
 * Spawns a single companion familiar at the player's position. 45 HP, 6 damage
 * per hit, semi-autonomous targeting (follows player within 2 tiles, attacks
 * enemies within 3 tiles, 1s recovery between swings). The familiar persists
 * until killed — only one companion at a time. Activating while the familiar
 * is alive has no effect.
 *
 * Upgrade hooks (resolved here, values come from UpgradeEffectResolver):
 * - Fortify (bind_fortify): hpBonus additive (+25)
 * - Resonant (bind_resonance): damageBonus additive (+5), adaptedBehavior boolean
 *
 * When adaptedBehavior is true, the familiar gains a secondary on-hit effect
 * based on the player's highest non-Conjuration tree:
 *   - fire: apply burning status (2s) per hit
 *   - lightning: chain to one adjacent enemy within 1.5 tiles for 50% damage
 *   - grit: 30% stagger chance (500ms) per hit
 *   - other: no adapted behavior
 * The behavior is determined once at spawn time — not re-evaluated per attack.
 *
 * The companion's AI behavior (follow, pursue, attack cycle, intercept) is
 * handled by the integration layer. This skill is responsible only for the
 * spawn/despawn lifecycle and resolver key resolution.
 *
 * The integration layer calls `onFamiliarDied()` when the companion dies,
 * wired to the SummonSystem's 'died' event for the companion archetype.
 *
 * Medium cooldown tier (1200ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Bind
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Adapted behavior type, determined by the player's highest non-Conjuration
 * tree at spawn time. Controls the familiar's secondary on-hit effect.
 */
export type AdaptedBehaviorType = 'fire' | 'lightning' | 'grit' | 'none';

/** On-hit behavior config baked into the companion at spawn. */
export interface AdaptedBehaviorConfig {
  readonly type: AdaptedBehaviorType;
  /** Fire: burning status duration in ms. */
  readonly burningDurationMs?: number;
  /** Lightning: max chain targets (always 1 for Resonant). */
  readonly chainMaxTargets?: number;
  /** Lightning: max chain range in tiles. */
  readonly chainRangeTiles?: number;
  /** Lightning: chain damage as fraction of familiar base damage. */
  readonly chainDamageFraction?: number;
  /** Grit: probability of stagger per hit (0–1). */
  readonly staggerChance?: number;
  /** Grit: stagger duration in ms. */
  readonly staggerDurationMs?: number;
}

/** Config object passed to SummonSystem.spawn() for companion summons. */
export interface CompanionSummonConfig {
  readonly id: string;
  readonly archetype: 'companion';
  readonly baseHp: number;
  readonly baseDamage: number;
  readonly baseSpeed: number;
  readonly leashRadiusTiles: number;
  readonly aggroWeight: number;
  /**
   * AI movement/combat mode. Consumed by the integration layer;
   * SummonSystem itself is movement-mode agnostic.
   * - 'follow-and-attack': follow player, engage enemies within attack range (Bind tier)
   * - 'tactical': stunned-target priority, retreat when hurt, threat positioning (Empower tier)
   * - 'manifest': tactical + grudge list targeting, faster intercepts, adapted abilities (Manifest apotheosis)
   */
  readonly aiMode?: 'follow-and-attack' | 'tactical' | 'manifest';
  /** When true, prefer staggered/stunned enemies over closer healthy ones. */
  readonly stunTargetPriority?: boolean;
  /** HP fraction (0–1) below which the familiar retreats to follow mode. */
  readonly retreatHpThreshold?: number;
  /** When true, familiar positions between player and most dangerous enemy. */
  readonly threatPositioning?: boolean;
  /** Max distance from familiar to target before engaging (tiles). */
  readonly attackRangeTiles?: number;
  /** Delay between swings in ms. */
  readonly attackRecoveryMs?: number;
  /**
   * Intercept behavior (Fortify upgrade). When enabled, the familiar
   * interposes itself between the player and incoming melee attacks
   * if within range, absorbing the hit. Consumed by the integration layer.
   */
  readonly interceptEnabled?: boolean;
  /** Minimum ms between intercept triggers. */
  readonly interceptCooldownMs?: number;
  /** Familiar will not intercept if its HP is below this threshold. */
  readonly interceptMinHp?: number;
  /**
   * Adapted behavior (Resonant upgrade). Determined at spawn time from the
   * player's highest non-Conjuration tree. Consumed by the integration layer
   * to apply secondary on-hit effects (burning, chain, stagger).
   */
  readonly adaptedBehavior?: AdaptedBehaviorConfig;
  /**
   * Dual adapted behaviors (Chimera tier). Two behaviors resolved from two
   * randomly-selected non-Conjuration trees at spawn time. Pre-Synthesis:
   * the integration layer alternates between them per attack. Post-Synthesis:
   * both fire simultaneously on each hit.
   */
  readonly dualBehaviors?: readonly [AdaptedBehaviorConfig, AdaptedBehaviorConfig];
  /** When true, both dual behaviors fire on the same hit (Synthesis upgrade). */
  readonly dualBehaviorSimultaneous?: boolean;
  /**
   * Overdrive behavior (Apex upgrade). When the familiar's HP drops below
   * the threshold ratio, overdrive activates for a fixed duration — applying
   * a damage multiplier. Cannot re-trigger until healed above threshold.
   */
  readonly overdriveMultiplier?: number;
  /** Duration of overdrive in ms once triggered. */
  readonly overdriveDurationMs?: number;
  /** HP ratio (0–1) below which overdrive activates. */
  readonly overdriveHpThreshold?: number;
  /**
   * Scaling factor for adapted ability power (0–1). Manifest apotheosis uses
   * 0.7, meaning adapted secondary effects fire at 70% of the player's current
   * stats rather than the familiar's own values. Consumed by the integration layer.
   */
  readonly adaptedAbilityPower?: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BindDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getMinEffect(skillId: string, key: string): number | undefined;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    /** Returns true if the companion should fail to manifest (XP-based freshness check). */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: CompanionSummonConfig, x: number, y: number): string;
  };
  readonly progression: {
    /** Returns the tree ID with the most XP among all non-Conjuration trees, or null if none. */
    getHighestNonConjurationTree(): string | null;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface BindSkillConfig {
  readonly cooldownMs: number;
  /** Base companion HP before upgrade bonuses. */
  readonly baseHp: number;
  /** Base companion damage per hit before upgrade bonuses. */
  readonly baseDamage: number;
  /** Base companion movement speed. */
  readonly baseSpeed: number;
  /** Max distance from player before returning (tiles). */
  readonly leashRadiusTiles: number;
  /** Max distance from familiar to target before engaging (tiles). */
  readonly attackRangeTiles: number;
  /** Delay between swings in ms. */
  readonly attackRecoveryMs: number;
  /** How much enemy aggro the familiar draws (0–1). */
  readonly baseAggroWeight: number;
}

const DEFAULT_CONFIG: BindSkillConfig = {
  cooldownMs: 1200,            // medium cooldown tier
  baseHp: 45,                  // spec: 45 HP
  baseDamage: 6,               // spec: 6 damage per hit
  baseSpeed: 1.0,              // integration layer applies px/s
  leashRadiusTiles: 2,         // spec: stay within 2 tiles of player
  attackRangeTiles: 3,         // spec: attack enemies within 3 tiles
  attackRecoveryMs: 1000,      // spec: 1s recovery between swings
  baseAggroWeight: 0.3,        // moderate aggro draw
};

// ---------------------------------------------------------------------------
// Adapted behavior resolution
// ---------------------------------------------------------------------------

/**
 * Maps a tree ID to the familiar's secondary on-hit behavior config.
 * Called once at spawn time — the behavior is baked into the companion
 * for its entire lifetime. Exported for reuse by EmpowerSkillEffect.
 */
export function resolveAdaptedBehavior(treeId: string | null): AdaptedBehaviorConfig {
  switch (treeId) {
    case 'fire':
      return {
        type: 'fire',
        burningDurationMs: 2000,
      };
    case 'lightning':
      return {
        type: 'lightning',
        chainMaxTargets: 1,
        chainRangeTiles: 1.5,
        chainDamageFraction: 0.5,
      };
    case 'grit':
      return {
        type: 'grit',
        staggerChance: 0.3,
        staggerDurationMs: 500,
      };
    default:
      return { type: 'none' };
  }
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BindSkillEffect implements SkillEffect {
  readonly skillId = 'bind';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: BindDeps;
  private readonly config: BindSkillConfig;

  private _cooldownRemaining = 0;
  private _familiarAlive = false;
  private _familiarId = '';
  private _fizzled = false;
  private _spawnsCount = 0;
  private _resolvedHp = 0;
  private _resolvedDamage = 0;
  private _interceptEnabled = false;
  private _interceptCooldownMs = 0;
  private _interceptMinHp = 0;
  private _adaptedBehavior: AdaptedBehaviorConfig = { type: 'none' };

  constructor(deps: BindDeps, config: Partial<BindSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get familiarAlive(): boolean { return this._familiarAlive; }
  get familiarId(): string { return this._familiarId; }
  get fizzled(): boolean { return this._fizzled; }
  get spawnsCount(): number { return this._spawnsCount; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get interceptEnabled(): boolean { return this._interceptEnabled; }
  get interceptCooldownMs(): number { return this._interceptCooldownMs; }
  get interceptMinHp(): number { return this._interceptMinHp; }
  get adaptedBehavior(): AdaptedBehaviorConfig { return this._adaptedBehavior; }

  // --- Integration layer hook ---

  /**
   * Called by the integration layer when the companion dies.
   * Wired to SummonSystem's 'died' event for archetype 'companion'.
   */
  onFamiliarDied(): void {
    this._familiarAlive = false;
    this._familiarId = '';
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._familiarAlive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const pos = this.deps.player.getPosition();

    // Resolve HP: base + additive bonus from Fortify
    const hpBonus = this.deps.resolver.getAdditiveEffect('bind', 'hpBonus');
    const resolvedHp = this.config.baseHp + hpBonus;
    this._resolvedHp = resolvedHp;

    // Resolve damage: base + additive bonus from Resonant
    const damageBonus = this.deps.resolver.getAdditiveEffect('bind', 'damageBonus');
    const resolvedDamage = this.config.baseDamage + damageBonus;
    this._resolvedDamage = resolvedDamage;

    // Resolve intercept behavior (Fortify upgrade)
    const interceptEnabled = this.deps.resolver.hasEffect('bind', 'interceptEnabled');
    this._interceptEnabled = interceptEnabled;

    const interceptCooldownMs = interceptEnabled
      ? (this.deps.resolver.getMinEffect('bind', 'interceptCooldownMs') ?? 2000)
      : 0;
    this._interceptCooldownMs = interceptCooldownMs;

    const interceptMinHp = interceptEnabled
      ? (this.deps.resolver.getMaxEffect('bind', 'interceptMinHp') ?? 15)
      : 0;
    this._interceptMinHp = interceptMinHp;

    // Resolve adapted behavior (Resonant upgrade)
    const adaptedBehaviorEnabled = this.deps.resolver.hasEffect('bind', 'adaptedBehavior');
    this._adaptedBehavior = adaptedBehaviorEnabled
      ? resolveAdaptedBehavior(this.deps.progression.getHighestNonConjurationTree())
      : { type: 'none' };

    const companionConfig: CompanionSummonConfig = {
      id: 'bound_companion',
      archetype: 'companion',
      baseHp: resolvedHp,
      baseDamage: resolvedDamage,
      baseSpeed: this.config.baseSpeed,
      leashRadiusTiles: this.config.leashRadiusTiles,
      aggroWeight: this.config.baseAggroWeight,
      aiMode: 'follow-and-attack',
      attackRangeTiles: this.config.attackRangeTiles,
      attackRecoveryMs: this.config.attackRecoveryMs,
      ...(interceptEnabled && {
        interceptEnabled: true,
        interceptCooldownMs,
        interceptMinHp,
      }),
      ...(this._adaptedBehavior.type !== 'none' && {
        adaptedBehavior: this._adaptedBehavior,
      }),
    };

    this._familiarId = this.deps.summons.spawn(companionConfig, pos.x, pos.y);
    this._familiarAlive = true;
    this._spawnsCount++;

    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._familiarAlive = false;
    this._familiarId = '';
  }
}
